import {useChatStateValues} from '@github-ui/copilot-chat/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useIsScrolledToEnd} from '@github-ui/copilot-chat/hooks/use-is-scrolled-to-end'
import {isFileReference, referencesAreEqual} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {useGetChatState} from '@github-ui/copilot-chat/utils/ObservableChatContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useLayoutContext} from '@github-ui/pacer/Layout/layout-context'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ScrollContainer} from '@github-ui/scroll-container'
import {usePreviousValue} from '@github-ui/use-previous-value'
import {FocusKeys} from '@primer/behaviors'
import {
  FileZipIcon,
  KebabHorizontalIcon,
  ListUnorderedIcon,
  MaximizeIcon,
  MinimizeIcon,
  XIcon,
} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, useFocusZone} from '@primer/react'
import {downloadZip} from 'client-zip'
import {clsx} from 'clsx'
import {
  type RefObject,
  startTransition,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  type WheelEvent,
} from 'react'

import {ErrorFallback} from '../ErrorFallback'
import styles from './ContentPreview.module.css'
import {useContentPreview, useContentPreviewOpen, useResourcePanelOpen} from './ContentPreviewContext'
import {ContentPreviewItem} from './ContentPreviewItem'
import {ContentPreviewTab} from './ContentPreviewTab'
import {downloadFile} from './download-file'
import {EmptyState} from './EmptyState'
import {LoadingState} from './LoadingState'
import {sendContentPreviewEvent} from './telemetry'

interface ContentPreviewProps {
  /** Element to focus on close. */
  returnFocusRef: RefObject<HTMLElement | null>
  /** Disable the resource panel feature. */
  disableResourcePanel?: boolean
  disableActionsMenu?: boolean
  /** Hide the expand workbench button */
  hideExpandButton?: boolean
}

/** Hook to close preview pane and collapse secondary column if expanded */
function useClosePreviewPane() {
  const {closePreviewPane} = useContentPreview()
  const {secondaryColumnFullWidth, toggleSecondaryColumnFullWidth, setDesktopLayoutState} = useLayoutContext()

  return useCallback(() => {
    if (secondaryColumnFullWidth) {
      toggleSecondaryColumnFullWidth()
      setDesktopLayoutState(prev => ({...prev, fullWidth: false}))
    }
    closePreviewPane()
  }, [closePreviewPane, secondaryColumnFullWidth, toggleSecondaryColumnFullWidth, setDesktopLayoutState])
}

function ContentPreviewInner({
  returnFocusRef,
  disableResourcePanel = false,
  disableActionsMenu = false,
  hideExpandButton = false,
}: ContentPreviewProps) {
  const isOpen = useContentPreviewOpen()
  const isResourcePanelOpen = useResourcePanelOpen()
  const {selectedThreadID, optedInToPreviewFeatures} = useChatStateValues(
    'selectedThreadID',
    'optedInToPreviewFeatures',
  )
  const getChatState = useGetChatState()
  const manager = useChatManager()
  const prevSelectedThreadID = usePreviousValue(selectedThreadID)
  const {
    items,
    allOpenItems: openItems,
    selectedItem,
    openItem,
    closeItem,
    closeItems,
    showLoadingState,
    openResourcePanel,
    returnFocusElementRef,
    ephemeralTab,
    promoteTab,
  } = useContentPreview()
  const handleClosePreviewPane = useClosePreviewPane()
  const prevIsOpen = usePreviousValue(isOpen)
  const isOpening = isOpen && !prevIsOpen
  const isClosing = prevIsOpen && !isOpen
  const openFiles = openItems.map(itemId => items.get(itemId)).filter(item => item?.type === 'file')

  const activeTabId = useId()
  const selectedTabPanelId = useId()
  const closeTabControlHintId = useId()

  const openItemInstances = openItems.map(key => items.get(key)).filter(item => !!item)
  const selectedItemInstance = openItemInstances.find(item => item.id === selectedItem) ?? openItemInstances[0]
  const previousSelectedItem = usePreviousValue(selectedItem)

  const resourcePanelSupported =
    copilotFeatureFlags.copilotResourcePanel && optedInToPreviewFeatures && !disableResourcePanel
  const isFocusingFirstTimeRef = useRef(true)

  useEffect(() => {
    // Return focus when:
    // 1. Closing the preview pane, or
    // 2. After workbench has opened and a file is loaded (if feature flag is enabled)
    if (
      isClosing ||
      (copilotFeatureFlags.focusTextAreaInsteadOfWorkbench &&
        isFocusingFirstTimeRef.current &&
        selectedItemInstance &&
        selectedItemInstance.type === 'file' &&
        !isOpening)
    ) {
      isFocusingFirstTimeRef.current = false
      // Use the stored returnFocusElement if available, otherwise fall back to returnFocusRef
      const returnFocusElement = returnFocusElementRef.current
      if (returnFocusElement) {
        returnFocusElement.focus()
      } else {
        returnFocusRef.current?.focus()
      }
    }
  }, [isClosing, isOpening, returnFocusRef, selectedItemInstance, returnFocusElementRef])

  useEffect(() => {
    // close preview pane when changing threads
    if (prevSelectedThreadID != null && prevSelectedThreadID !== selectedThreadID) {
      handleClosePreviewPane()
    }
  }, [handleClosePreviewPane, prevSelectedThreadID, selectedThreadID])

  useLayoutEffect(() => {
    // keep selected tab in view when changing tabs
    if (!selectedItem || !isOpen) return

    const index = openItemInstances.findIndex(item => item.id === selectedItem)
    const tab = tabsRef.current[index]
    tab?.scrollIntoView({block: 'nearest', inline: 'nearest'})
  }, [selectedItem, openItemInstances, isOpen])

  useEffect(() => {
    // open resource panel if an eligible item is selected
    if (
      resourcePanelSupported &&
      selectedItem &&
      selectedItem !== previousSelectedItem &&
      !isResourcePanelOpen &&
      selectedItemInstance?.type === 'file' &&
      isFileReference(selectedItemInstance.reference)
    ) {
      openResourcePanel()
    }
  }, [
    isResourcePanelOpen,
    openResourcePanel,
    previousSelectedItem,
    resourcePanelSupported,
    selectedItem,
    selectedItemInstance,
  ])

  useEffect(() => {
    if (!copilotFeatureFlags.copilotResourcePanel || !optedInToPreviewFeatures) return
    if (selectedItem === previousSelectedItem) return
    const {currentReferences, implicitReferences} = getChatState()
    // automatically add implicit reference to selected tab.
    if (selectedItem && selectedItemInstance?.type === 'file') {
      const reference = selectedItemInstance.reference
      if (isFileReference(reference) && !currentReferences.some(ref => referencesAreEqual(ref, reference))) {
        if (implicitReferences.size > 0) {
          manager.clearImplicitReferences()
        }
        manager.addReference(reference, 'ContentPreview', {implicit: true})
      }
    } else if (implicitReferences.size > 0) {
      manager.clearImplicitReferences()
    }
  }, [getChatState, manager, optedInToPreviewFeatures, previousSelectedItem, selectedItem, selectedItemInstance])

  const downloadAll = async () => {
    const files = openFiles.map(item => new File([item.value], item.name))
    const blob = await downloadZip(files).blob()
    downloadFile(new File([blob], 'files.zip'))
  }

  const handleClose = useCallback(
    (closePreview = false) => {
      if (selectedItemInstance) {
        const shouldClosePreview = closePreview && openItemInstances.length === 1
        closeItem(selectedItemInstance.id)
        if (shouldClosePreview) {
          handleClosePreviewPane()
          sendEvent('dotcom_chat.activate', {target: 'BROWSER_CLOSE', mode: 'immersive'})
        }
      }
    },

    [closeItem, handleClosePreviewPane, openItemInstances.length, selectedItemInstance],
  )

  const tabsRef = useRef<Array<HTMLDivElement | null>>([])

  const tablistRef = useRef<HTMLDivElement>(null)
  useFocusZone(
    {
      containerRef: tablistRef,
      focusableElementFilter: el => el.role === 'tab',
      bindKeys: FocusKeys.ArrowHorizontal | FocusKeys.HomeAndEnd,
      focusInStrategy: 'first',
      focusOutBehavior: 'wrap',
    },
    [openItemInstances],
  )
  // We don't need that when we use the new ScrollContainer component
  const tabsHasOverflow = !useIsScrolledToEnd(tablistRef) && !copilotFeatureFlags.scrollPreviewTabs

  // Also don't need this when using the ScrollContainer, as it has its own onWheel handler
  const onTabsWheel = (event: WheelEvent<HTMLDivElement>) => {
    if (!event.deltaY) return
    event.preventDefault()
    event.currentTarget.scrollLeft += event.deltaY
  }

  const closeTab = (i: number) => {
    const item = openItemInstances[i]
    if (!item) return

    // Remove implicit reference when its tab is closed before being promoted
    if (item.type === 'file' && isFileReference(item.reference)) {
      const {implicitReferences} = getChatState()
      const implicitRef = [...implicitReferences].find(ref => referencesAreEqual(ref, item.reference))
      if (implicitRef) {
        manager.clearImplicitReferences()
      }
    }

    const nextFocusTab = tabsRef.current[i + 1] ?? tabsRef.current[i - 1] ?? tablistRef.current
    const nextFocusTarget = nextFocusTab?.querySelector('[role="tab"]') as HTMLElement | null
    nextFocusTarget?.focus()

    const isOnly = openItemInstances.length === 1
    closeItem(item.id)
    if (isOnly) handleClosePreviewPane()
    sendContentPreviewEvent('filePreviewTab.close', item)
  }

  const closeAll = () => {
    const {implicitReferences} = getChatState()
    if (implicitReferences.size > 0) {
      manager.clearImplicitReferences()
    }
    closeItems()
    handleClosePreviewPane()
    sendEvent('dotcom_chat.activate', {target: 'BROWSER_CLOSE_ALL_TABS', mode: 'immersive'})
  }

  return (
    <>
      <div
        className={clsx(styles.toolbar, copilotFeatureFlags.copilotWorkbenchSlimLineTopTabs && styles.slimLineTopTabs)}
      >
        {resourcePanelSupported && !isResourcePanelOpen && (
          <div className={clsx(styles.actions, styles.rightDivider)}>
            <IconButton
              icon={ListUnorderedIcon}
              aria-label="Open resource panel"
              onClick={() => {
                openResourcePanel()
                sendEvent('dotcom_chat.activate', {
                  target: 'RESOURCE_PANEL_OPEN',
                  mode: 'immersive',
                  source: 'workbench_button',
                })
              }}
              variant="invisible"
            />
          </div>
        )}
        <span id={closeTabControlHintId} aria-hidden="true" className="sr-only">
          Press Delete to close the focussed tab.
        </span>
        <TabsContainer tabsListRef={tablistRef} onTabsWheel={onTabsWheel}>
          {openItemInstances.map((item, i) => (
            <ContentPreviewTab
              id={item.id === selectedItem ? activeTabId : undefined}
              tabPanelId={item.id === selectedItem ? selectedTabPanelId : undefined}
              controlHintId={closeTabControlHintId}
              key={item.id}
              isActive={item.id === selectedItem}
              isEphemeral={item.id === ephemeralTab}
              onClick={() => {
                if (copilotFeatureFlags.filePreviewKeepMounted) {
                  startTransition(() => openItem(item.id))
                } else {
                  openItem(item.id)
                }
                sendContentPreviewEvent('filePreviewTab.click', item)
              }}
              onDoubleClick={() => {
                if (item.type === 'file' && isFileReference(item.reference)) {
                  const {implicitReferences} = getChatState()
                  const implicitRef = [...implicitReferences].find(ref => referencesAreEqual(ref, item.reference))
                  if (implicitRef) {
                    manager.promoteImplicitReference(implicitRef)
                    sendEvent('dotcom_chat.activate', {
                      target: 'IMPLICIT_REFERENCE_PROMOTED',
                      mode: 'immersive',
                      source: 'tab_double_click',
                    })
                  }
                }
                promoteTab(item.id)
              }}
              onClose={() => closeTab(i)}
              item={item}
              ref={el => {
                tabsRef.current[i] = el
              }}
            />
          ))}
        </TabsContainer>
        <div className={clsx(styles.actions, tabsHasOverflow && styles.leftDivider)}>
          {!disableActionsMenu && openItems.length > 0 && (
            <ActionMenu>
              <ActionMenu.Anchor>
                <IconButton icon={KebabHorizontalIcon} aria-label="More options" variant="invisible" />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay>
                <ActionList>
                  {openFiles.length > 0 && (
                    <ActionList.Item onSelect={downloadAll}>
                      <ActionList.LeadingVisual>
                        <FileZipIcon />
                      </ActionList.LeadingVisual>
                      Download all files
                    </ActionList.Item>
                  )}
                  <ActionList.Item onSelect={closeAll}>
                    <ActionList.LeadingVisual>
                      <XIcon />
                    </ActionList.LeadingVisual>
                    Close all tabs
                  </ActionList.Item>
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
          <CloseContentPreviewButton hideExpandButton={hideExpandButton} />
        </div>
      </div>

      <div
        className={clsx(styles.content, 'content-preview-content')}
        role="tabpanel"
        id={selectedTabPanelId}
        aria-labelledby={activeTabId}
      >
        {showLoadingState ? (
          <LoadingState />
        ) : selectedItemInstance ? (
          <ContentPreviewItem isOpening={isOpening} previewItem={selectedItemInstance} onClose={handleClose} />
        ) : (
          <EmptyState isOpening={isOpening} />
        )}
      </div>
    </>
  )
}

function CloseContentPreviewButton({hideExpandButton = false}: {hideExpandButton?: boolean}) {
  const handleClosePreviewPane = useClosePreviewPane()
  const {secondaryColumnFullWidth, toggleSecondaryColumnFullWidth} = useLayoutContext()

  return (
    <div className="d-flex gap-1">
      {!hideExpandButton && (
        <IconButton
          variant="invisible"
          icon={secondaryColumnFullWidth ? MinimizeIcon : MaximizeIcon}
          aria-label={secondaryColumnFullWidth ? 'Collapse workbench' : 'Expand workbench'}
          onClick={() => {
            toggleSecondaryColumnFullWidth()
          }}
          className={styles.onlyShowOnLargeViewports}
        />
      )}
      <IconButton
        variant="invisible"
        icon={XIcon}
        aria-label="Close workbench"
        onClick={() => {
          handleClosePreviewPane()
          sendEvent('dotcom_chat.activate', {target: 'CLOSE_PREVIEW_PANE_BUTTON', mode: 'immersive'})
        }}
      />
    </div>
  )
}

function ContentPreviewFallback() {
  return (
    <>
      <div className={styles.toolbar}>
        <div className={clsx(styles.actions, styles.rightDivider)}>
          <CloseContentPreviewButton />
        </div>
      </div>
      <div className={clsx(styles.content)}>
        <ErrorFallback regionName="The workbench" />
      </div>
    </>
  )
}

// We put as much logic into the Inner component as possible so that errors are always caught. We still need some
// container styles around the error boundary though to contain it if it does appear.
export function ContentPreview({
  returnFocusRef,
  disableResourcePanel = false,
  disableActionsMenu = false,
  hideExpandButton = false,
  className,
}: ContentPreviewProps & {className?: string}) {
  const labelId = useId()
  const {contentPreviewRef} = useContentPreview()
  const contentPreviewOpen = useContentPreviewOpen()
  const isResourcePanelOpen = useResourcePanelOpen()

  return (
    <div
      aria-labelledby={labelId}
      className={clsx(
        styles.container,
        copilotFeatureFlags.copilotWorkbenchSlimLineTopTabs && styles.slimLineTopTabs,
        isResourcePanelOpen && styles.resourcePanelOpen,
        className,
      )}
      tabIndex={-1}
      data-testid="content-preview"
      role="dialog"
      aria-modal="false"
      ref={contentPreviewOpen ? contentPreviewRef : undefined}
    >
      <h2 id={labelId} className="sr-only">
        Workbench
      </h2>
      <ErrorBoundary fallback={<ContentPreviewFallback />}>
        <ContentPreviewInner
          returnFocusRef={returnFocusRef}
          disableResourcePanel={disableResourcePanel}
          disableActionsMenu={disableActionsMenu}
          hideExpandButton={hideExpandButton}
        />
      </ErrorBoundary>
    </div>
  )
}

interface TabsContainerProps {
  tabsListRef: React.RefObject<HTMLDivElement | null>
  onTabsWheel: (event: React.WheelEvent<HTMLDivElement>) => void
  children: React.ReactNode
}

const TabsContainer = ({tabsListRef, onTabsWheel, children}: TabsContainerProps) => {
  const scrollContainerEnabled = copilotFeatureFlags.scrollPreviewTabs

  if (scrollContainerEnabled) {
    return (
      <div className={styles.tabsScrollWrapper}>
        <ScrollContainer
          ref={tabsListRef}
          direction="horizontal"
          attributesForContentDiv={{
            className: styles.scrollContainerTabs,
            role: 'tablist',
            'aria-label': 'Workbench items',
          }}
        >
          {children}
        </ScrollContainer>
      </div>
    )
  }

  return (
    <div className={styles.tabsContainer}>
      <div
        className={styles.tabs}
        role="tablist"
        aria-label="Workbench items"
        ref={tabsListRef}
        tabIndex={-1}
        onWheel={onTabsWheel}
      >
        {children}
      </div>
    </div>
  )
}
